import { useSuspenseQueries, useSuspenseQuery } from '@tanstack/react-query'
import { QuotaType } from '@bpinternal/const'
import {
  workspacesUsagesQueryOptions,
  usageBreakdownUsageQueryOptions,
  workspaceUsagesQueryOptions,
} from '../../queries/usage'
import { Usage } from '@botpress/client'
import { getCurrentLocalDateString } from '~/utils'

type UsageParams<T extends QuotaType> = {
  workspaceId: string
  period?: string
  quotas: T[]
}

export function useUsage<T extends QuotaType>({
  workspaceId,
  quotas,
  period = getCurrentLocalDateString(),
}: UsageParams<T>) {
  return useSuspenseQuery(workspaceUsagesQueryOptions<T>({ types: quotas, workspaceId, period })).data
}

type WorkspacesUsagesParams<T extends QuotaType> = {
  workspaceIds: string[]
  period?: string
  quotas: T[]
}

export function useWorkspacesUsages<T extends QuotaType>({
  workspaceIds,
  quotas,
  period = getCurrentLocalDateString(),
}: WorkspacesUsagesParams<T>) {
  return useSuspenseQuery(workspacesUsagesQueryOptions<T>({ workspaceIds, types: quotas, period })).data
}

function useUsageBreakdown<T extends QuotaType>({
  workspaceId,
  quotas,
  period = getCurrentLocalDateString(),
}: UsageParams<T>) {
  return useSuspenseQueries({
    queries: quotas.map((type) => usageBreakdownUsageQueryOptions({ type, workspaceId, period })),
    combine: (results) => {
      return results.reduce(
        (acc, result) => {
          if (result.data) {
            acc[result.data.type as T] = result.data.usages
          }
          return acc
        },
        {} as Record<T, { botId: string; value: number }[]>
      )
    },
  })
}

export function useDetailedUsage<T extends QuotaType>({
  workspaceId,
  quotas,
  period = getCurrentLocalDateString(),
}: UsageParams<T>) {
  const baseUsage = useUsage({ workspaceId, quotas, period })
  const breakdown = useUsageBreakdown({ workspaceId, quotas, period })
  return Object.keys(baseUsage).reduce(
    (acc, key) => {
      acc[key as T] = { ...baseUsage[key as T], breakdown: breakdown[key as T] }
      return acc
    },
    {} as Record<T, Usage & { breakdown: { botId: string; value: number }[] }>
  )
}
